import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useAsync } from '@/components/useAsync'
import { Section } from '@/components/Section'
import { Picker } from '@/components/Picker'
import { AgentCount, agentCountLabel } from '@/components/TaskPipeline'
import { TaskRows } from '@/components/TaskRows'
import { PIPELINE_STAGES, REVIEW_STAGES, intakeNeedsDeveloper, needsScienceDeveloper, pipelineCounts, taskDisplayStage } from '@/lib/stages'
import { taskPrograms } from '@/lib/programs'
import { IntakeRows } from '@/pages/scientific/IntakeRows'
import type { IntakeSummary } from '@/lib/templateIntakes'
import type { EngOverview, Task } from '@/lib/types'
import './pipeline.css'

export default function EngDashboard() {
  const [params, setParams] = useSearchParams()
  const program = params.get('program') ?? ''
  // The overview omits published tasks from my_tasks; rows=1 adds every listed task (including
  // published work) so program totals and stage links cover the whole directory in one request.
  const { data, error, loading } = useAsync(async () => {
    const overview = await api.get<EngOverview & { tasks: Task[]; template_intakes_watching?: IntakeSummary[] }>('/eng/overview?rows=1')
    return { overview, tasks: overview.tasks }
  })
  if (loading) return <p className="muted">Loading…</p>
  if (error || !data) return <div className="note err">{error}</div>

  const allTasks = data.tasks.filter(t => t.lifecycle !== 'retired')
  // Experts own these intakes; the developer view is read-only and never a queue entry.
  const allTemplates = data.overview.template_intakes_watching ?? []
  const matchesProgram = (item: { programs?: string[]; program?: string }) => !program || taskPrograms(item).includes(program)
  const tasks = allTasks.filter(matchesProgram)
  const templates = allTemplates.filter(matchesProgram)
  // Quality-check decisions, and science pipelines that stopped; everything else moves on its own.
  const actionable = tasks.filter(t => t.stage === 4 && !t.active_review_id || needsScienceDeveloper(t))
  const waiting = tasks.filter(t => t.stage !== 5 && !actionable.includes(t))
  const stuckTemplates = templates.filter(intakeNeedsDeveloper)
  const programs = [...new Set([...allTasks, ...allTemplates].flatMap(taskPrograms).concat(program ? [program] : []))].sort()
  const scientific = program.toLowerCase() === 'physics'
  // Physics uses the same six-stage board as the experts, counts and all; other programs keep
  // the task flow's stored stages.
  const counts = pipelineCounts(tasks, templates)
  const stages = scientific
    ? PIPELINE_STAGES.map(s => ({ key: s.key, label: `${s.num}. ${s.label}`, count: counts[s.key].total, agentCount: s.agent ? counts[s.key] : null }))
    : REVIEW_STAGES.map(s => ({ key: String(s.stage), agentCount: null,
        label: `${s.num}. ${s.stage === 0 ? 'Draft' : s.stage === 3 ? 'Build & rollouts' : s.stage === 4 ? 'Review & quality check' : s.label}`,
        count: tasks.filter(t => taskDisplayStage(t) === s.stage).length }))
  const taskCount = (n: number) => `${n} total`

  return (
    <div>
      <div className="page-head">
        <div><h1>Pipeline</h1></div>
        <Picker label="Program" value={program} onChange={value => {
          const next = new URLSearchParams(params)
          if (value) next.set('program', value); else next.delete('program')
          setParams(next)
        }} options={[
          { value: '', label: 'All programs', detail: taskCount(allTasks.length) },
          ...programs.map(value => ({ value, label: value, detail: taskCount(allTasks.filter(t => taskPrograms(t).includes(value)).length) })),
        ]} />
      </div>

      <nav className="flow developer-pipeline" aria-label="Pipeline by stage">
        {stages.map(s => {
          const query = new URLSearchParams({ stage: s.key })
          if (program) query.set('program', program)
          return <div className="developer-pipeline-stage" key={s.key}>
            <Link to={`/eng/tasks?${query}`} aria-label={s.agentCount ? agentCountLabel(s.label, s.agentCount) : `${s.label}: ${s.count}`}>
              <span className="lbl">{s.label}</span>
              {s.agentCount ? <AgentCount count={s.agentCount} /> : <span className="num">{s.count}</span>}
            </Link>
          </div>
        })}
      </nav>

      <Section title="Needs your attention" count={actionable.length + stuckTemplates.length}>
        {(actionable.length > 0 || !stuckTemplates.length) && <TaskRows tasks={actionable} role="engineer" showTodo empty="No tasks need your attention." />}
        <IntakeRows templates={stuckTemplates} developer />
      </Section>
      <Section title="Waiting on the expert or the agent" count={waiting.length}><TaskRows tasks={waiting} role="engineer" showTodo empty="No tasks are waiting." /></Section>
    </div>
  )
}
